// AppShellOverlays — the floating surfaces that ride OVER the composed shell:
// the keep-shell boot banner, the meta corner + guided tour, every controlled
// Dialog driven by `overlay`, the content-finder, the ingest host, and the one
// toast slot. Extracted verbatim from AppShellHost's return (C1 #9); all facts +
// intents come off the AppShellModel context.

// The leaf `./identity` subpath (not the `@bilda/app` barrel): the barrel's
// `dist/index.js` re-exports the workflow modules too, which pull in
// `node:crypto`/`node:path` — fine for the server shell, but a browser
// production build can't polyfill `createHash` and the bundle fails. The
// identity string alone has no such dependency (see `turnLiveness`'s own
// subpath for the same precedent).
import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useRef } from "react";
import { Button } from "../../components/Button/Button.js";
import { Dialog } from "../../components/Dialog/Dialog.js";
import { Palette } from "../../components/Editor/Palette/Palette.js";
import { EmptyState } from "../../components/EmptyState/EmptyState.js";
import { ExportFlow } from "../../components/ExportFlow/ExportFlow.js";
import { Finder } from "../../components/Finder/Finder.js";
import { Help } from "../../components/Help/Help.js";
import { helpDocs } from "../../components/Help/helpContent.js";
import { HistoryInspection } from "../../components/HistoryInspection/HistoryInspection.js";
import { Icon } from "../../components/Icon/Icon.js";
import { IngestHost } from "../../components/IngestHost/IngestHost.js";
import { MaterialCreate } from "../../components/MaterialCreate/MaterialCreate.js";
import type { ResourceRef } from "../../components/Navigation/types.js";
import { ConfirmDiscard } from "../../components/ResourceTabs/ConfirmDiscard.js";
import { Text } from "../../components/Text/Text.js";
// The omnibar Dialog's own polish (auto-focus target aside): a compact
// single-row header, a roomier input, and a fix for the input's focus ring
// clipping at the Dialog body's left/right edges. Kept as its own tiny sheet
// rather than growing the shared Dialog.css, since these rules are scoped to
// ONE dialog instance (the `bilda-omnibar-dialog` class below).
import "./omnibarOverlay.css";
import { useAppShell } from "./context.js";
import { BootBanner } from "./gates.js";
import { MessageLayer } from "./MessageLayer.js";

// The guided tour — an on-request, cancellable-any-time coach-mark overlay that
// spotlights the REAL app (host-measured rects). Renders nothing at rest
// (idle). Mounted BEFORE the shell frame (AppShellHost), not with the other
// overlays: it is position-fixed, so DOM order only sets the Tab order, and
// the tour popup must come first, not after the panel and dock (G14,
// WCAG 2.4.3).
export const CoachOverlay = (): ReactNode => useAppShell().coach.overlay;

export const AppShellOverlays = (): ReactNode => {
    const { t, i18n } = useLingui();
    const {
        frame,
        dispatch,
        ports,
        editor,
        libraryError,
        retryNow,
        closeOverlay,
        templateCatalog,
        sourceItems,
        createState,
        createSeedTitle,
        onCreateMaterial,
        showNotice,
        exportFormats,
        exportState,
        setExportState,
        exportReady,
        setExportReady,
        onExportStart,
        packageNow,
        enterFullChat,
        historyView,
        fileHistory,
        historyVersionId,
        setHistoryVersionId,
        onRequestFileHistory,
        onRestoreVersion,
        finderDocs,
        finderLoading,
        onFinderJump,
        mkConversationId,
        confirmDelete,
        setConfirmDelete,
        deleteResourceNow,
        confirmDeleteConversation,
        setConfirmDeleteConversation,
        deleteConversationNow,
        archiveConversationNow,
        ingestHostRef,
        library,
        displayName,
        setLibrary,
        openRef,
        setIngest,
        sendChat,
        groundAddedSource,
        refFor,
        applyIngest,
        activeConversationId,
        recordIngestConversation,
        omnibar,
        tabs,
        studio,
    } = useAppShell();
    // On the Studio canvas the same item is never there twice.
    const onCanvas = tabs.active.type === "studio";
    const { overlay, confirmClose } = frame;

    // The omnibar's search input — focused explicitly on open (see the
    // Dialog's `initialFocusRef` below) so ⌘K lands the caret in the field
    // immediately, rather than on the header's close button (Radix's default
    // first-tabbable-element target, which precedes the input in DOM order).
    const commandInputRef = useRef<HTMLInputElement>(null);

    // The delete confirmation's consequence line, per resource kind (master
    // audit R5: the dialog NAMES what the deletion touches — derived materials,
    // citations — instead of a generic "are you sure").
    const deleteConsequence = (kind: ResourceRef["type"]): string => {
        switch (kind) {
            case "source":
                return t`Materials generated from this source keep their content, but citations pointing into it can no longer be verified and its grounding disappears.`;
            case "material":
                return t`The material leaves the workspace, along with its citations into your sources. Exports already exported from it stay downloadable.`;
            case "export":
                return t`The package leaves the workspace and can no longer be downloaded from here. Files you already downloaded are untouched.`;
            case "file":
                return t`The file leaves the workspace. Sources already made from it keep their content.`;
            default:
                return exhaustSwitchCase(kind);
        }
    };

    return (
        <>
            {/* Keep-shell boot/session banner (audit D1): reaching here means a
                library is loaded, so a terminal `libraryError` (a mid-session
                401, a malformed re-read, a domain refusal) floats OVER the
                still-usable shell with its one guaranteed recovery — it never
                replaced the workspace. */}
            {libraryError !== null ? (
                <BootBanner error={libraryError} onRetryNow={retryNow} />
            ) : null}

            {/* The version label lives at the foot of the sidebar
                (SidebarHost); About lives in Settings. */}

            {/* The creation dialog — "Nytt material" is a CREATE intent
                (template + title + optional source → createMaterial), never
                the block palette (master audit R4 / §1.3). */}
            <Dialog
                open={overlay.type === "create"}
                onOpenChange={(o) => {
                    if (!o) closeOverlay();
                }}
                title={t`New material`}
                description={t`Pick a template and name your material.`}
            >
                <MaterialCreate
                    catalog={templateCatalog}
                    sources={sourceItems}
                    state={createState}
                    initialTitle={createSeedTitle}
                    onCreate={onCreateMaterial}
                />
            </Dialog>

            {/* The block palette — the editor's insert surface, reachable only
                from the material toolbar's Insert (a material is always open
                and loaded when this shows). */}
            <Dialog
                open={overlay.type === "insert"}
                onOpenChange={(o) => {
                    if (!o) closeOverlay();
                }}
                title={t`Insert into material`}
                description={t`Add a block — what you can insert follows the template.`}
                footer={
                    <Button variant="ghost" onClick={closeOverlay}>
                        {t`Close`}
                    </Button>
                }
            >
                <Palette
                    shape={editor.editorShape}
                    onInsert={editor.onInsert}
                />
            </Dialog>

            <Dialog
                open={overlay.type === "export"}
                onOpenChange={(o) => {
                    if (!o) {
                        closeOverlay();
                        setExportState({ type: "choosing" });
                        setExportReady(null);
                    }
                }}
                title={t`Export material`}
                description={t`Export the material for a learning context.`}
                footer={
                    <Button variant="ghost" onClick={closeOverlay}>
                        {t`Close`}
                    </Button>
                }
            >
                <ExportFlow
                    formats={exportFormats}
                    state={exportState}
                    onExport={onExportStart}
                    onDownload={() => {
                        // Fetch + save the finished package through the port. Stay
                        // on `done` so the author can re-download or export again.
                        // Confirm the save with a toast (feedback F1: the download
                        // is otherwise silent — worse on desktop, no browser shelf).
                        if (exportReady !== null) {
                            ports.downloadExport(exportReady.downloadUrl);
                            if (exportState.type === "done") {
                                showNotice({
                                    type: "exported",
                                    filename: exportState.downloadName,
                                });
                            }
                        }
                    }}
                    onReset={() => {
                        setExportState({ type: "choosing" });
                        setExportReady(null);
                    }}
                    // The citation gate's override: proceed to packaging the
                    // format the author chose, despite the flagged claims.
                    onPackageAnyway={() => {
                        if (exportState.type === "review")
                            packageNow(exportState.format);
                    }}
                    // The gate's secondary exit: CLOSE the export dialog so the
                    // author lands back on the material, where the flagged
                    // statements actually live — resetting to the format picker would just
                    // loop them back into the same alarm (UX-eval P1). Reset the
                    // export state so a later re-open starts clean.
                    onRetry={() => {
                        if (exportState.type === "failed")
                            packageNow(exportState.format);
                    }}
                    onReviewFirst={() => {
                        setExportState({ type: "choosing" });
                        closeOverlay();
                    }}
                />
            </Dialog>

            {/* ⌘⇧H help — the Diátaxis documentation overlay (Tutorials / How-to
                / Reference / Explanation), promoted from sketches/help. Static
                content, no ports, no domain read — the only live wire is the
                footer's "Ask the assistant" bridge, which today just opens the
                assistant (enterFullChat); seeding the composer with the reading
                context is future work (no composer-prefill mechanism yet). */}
            <Help
                open={overlay.type === "help"}
                onOpenChange={(o) => {
                    if (!o) closeOverlay();
                }}
                docs={helpDocs((descriptor) => i18n._(descriptor))}
                onAskAssistant={() => {
                    closeOverlay();
                    enterFullChat();
                }}
            />

            {/* History & Inspection — the timeline + what-the-agent-knows
                surface, reached from the workspace band, the resource overflow's
                "Version history", and the palette. Data via loadHistory /
                loadFileHistory; restore via restoreResourceVersion. Over HTTP the
                reads are gapped (typed refusal → the calm empty state). */}
            <Dialog
                open={overlay.type === "history"}
                onOpenChange={(o) => {
                    if (!o) closeOverlay();
                }}
                title={t`History`}
                description={t`What happened, and what the assistant knows.`}
            >
                {historyView === null ? (
                    <EmptyState
                        title={t`History isn't available here yet`}
                        body={t`This is where you'll see what happened over time and what the assistant has learned. It isn't available here yet.`}
                    />
                ) : (
                    <HistoryInspection
                        sections={historyView.sections}
                        inspection={{
                            knowsYou: historyView.knowsYou,
                            triples: historyView.triples,
                            groundingMaterialTitle:
                                historyView.groundingMaterialTitle,
                            groundingElements: historyView.groundingElements,
                            groundingCoverage: historyView.groundingCoverage,
                        }}
                        onRequestFileHistory={onRequestFileHistory}
                        fileHistory={
                            fileHistory === null
                                ? undefined
                                : {
                                      resourceTitle:
                                          fileHistory.view.resourceTitle,
                                      versions: fileHistory.view.versions,
                                      selectedVersionId:
                                          historyVersionId ?? undefined,
                                      diff:
                                          historyVersionId === null
                                              ? undefined
                                              : fileHistory.view.diffs[
                                                    historyVersionId
                                                ],
                                      onSelectVersion: setHistoryVersionId,
                                      onRestore: (marker) =>
                                          onRestoreVersion(
                                              fileHistory.resourceKey,
                                              marker,
                                          ),
                                      onBackToCurrent: () =>
                                          setHistoryVersionId(null),
                                  }
                        }
                    />
                )}
            </Dialog>

            {/* The omnibar (⌘K) — TASK 3 (chat-primary shell) unmounted TopStrip,
                which was the omnibar's ONLY host (its bar input and the
                results listbox it anchored beneath itself): with no TopStrip,
                `open-overlay {command}` set state but nothing rendered it on
                EITHER platform. This Dialog gives it a real home again — a
                centred modal rather than a top-bar dropdown, since there is no
                top bar left to anchor to. The single combobox input reuses
                the `bilda-top-strip__*` field-frame classes (omnibarOverlay.css); `commandSurface` is the SAME pure results
                listbox (CommandPalette) TopStrip used to anchor. Reachable via
                the omnibar rail door, the ⌘K chord (desktop always; web via
                the dfd31976 exception), and any `openCommand`/`openShowAll`
                caller — all of which already just set this one overlay. */}
            <Dialog
                open={overlay.type === "command"}
                onOpenChange={(o) => {
                    if (!o) {
                        closeOverlay();
                        omnibar.resetCommandSurface();
                    }
                }}
                title={t`Search or run a command`}
                className="bilda-omnibar-dialog"
                initialFocusRef={commandInputRef}
            >
                <div className="bilda-top-strip__omnibar">
                    <span className="bilda-top-strip__glyph" aria-hidden="true">
                        <Icon name="search" />
                    </span>
                    <input
                        ref={commandInputRef}
                        type="text"
                        role="combobox"
                        className="bilda-top-strip__omnibar-input"
                        value={omnibar.commandQuery}
                        placeholder={t`Search or run a command`}
                        aria-label={t`Search or run a command`}
                        aria-expanded={omnibar.commandHasResults}
                        aria-controls={
                            omnibar.commandHasResults
                                ? omnibar.commandListboxId
                                : undefined
                        }
                        aria-activedescendant={
                            omnibar.commandActiveId !== null
                                ? omnibar.commandOptionId(
                                      omnibar.commandActiveId,
                                  )
                                : undefined
                        }
                        aria-autocomplete="list"
                        autoComplete="off"
                        spellCheck={false}
                        onChange={(event) =>
                            omnibar.onCommandQueryChange(event.target.value)
                        }
                        onKeyDown={omnibar.onCommandKeyDown}
                    />
                </div>
                {omnibar.commandSurface}
            </Dialog>

            {/* The fuzzy content-finder (⌘⇧F) — a centred two-pane modal that
                searches INTO content bodies and previews the hit in context.
                Distinct from the omnibar (title launcher): the finder owns the
                only preview pane. It renders its own scrim/focus-trap via Radix,
                so it sits outside the Dialog wrapper. */}
            <Finder
                open={overlay.type === "finder"}
                onOpenChange={(o) => {
                    if (!o) closeOverlay();
                }}
                docs={finderDocs}
                loading={finderLoading}
                onJump={onFinderJump}
            />

            {/* The workspace Search + Recent surface is no longer an overlay
                (dogfood F6): it is a full-page singleton CENTRE tab now
                (SearchCentre, Centre.tsx's `search` arm), opened by the rail's
                Recent/Search doors via `open-search`. */}

            <Dialog
                open={confirmClose !== null}
                onOpenChange={(o) => {
                    if (!o) dispatch({ type: "keep-unsaved-tab" });
                }}
                title={t`Close without saving?`}
            >
                {confirmClose !== null ? (
                    <ConfirmDiscard
                        tab={confirmClose}
                        onKeep={() => dispatch({ type: "keep-unsaved-tab" })}
                        onDiscard={() =>
                            // A discarded close is still a close — the fold
                            // remembers it on the reopen stack (fresh, no
                            // buffer).
                            dispatch({
                                type: "discard-unsaved-tab",
                                freshConversationId: mkConversationId(),
                            })
                        }
                    />
                ) : null}
            </Dialog>

            {/* The delete confirmation — names the consequences for the
                resource's kind before `deleteResource` runs; the undo toast
                after it makes the choice reversible (prevention #5 +
                freedom #3). */}
            <Dialog
                open={confirmDelete !== null}
                onOpenChange={(o) => {
                    if (!o) setConfirmDelete(null);
                }}
                title={
                    confirmDelete !== null
                        ? t`Delete “${confirmDelete.title}”?`
                        : t`Delete?`
                }
                footer={
                    confirmDelete !== null ? (
                        <>
                            <Button
                                variant="ghost"
                                onClick={() => setConfirmDelete(null)}
                            >
                                {t`Cancel`}
                            </Button>
                            <Button
                                variant="danger"
                                onClick={() => deleteResourceNow(confirmDelete)}
                            >
                                {t`Delete`}
                            </Button>
                        </>
                    ) : null
                }
            >
                {confirmDelete !== null ? (
                    <Text as="p" tone="dim">
                        {deleteConsequence(confirmDelete.type)}
                    </Text>
                ) : null}
            </Dialog>

            {/* The conversation delete confirmation — a lighter sibling of the
                resource one: a conversation delete is fully reversible (the
                whole thread is tombstoned, restorable), so the copy reassures
                rather than warns, and the undo toast makes it recoverable.
                "Archive instead" sets it aside without deleting (pen list
                pattern, decision 3). */}
            <Dialog
                open={confirmDeleteConversation !== null}
                onOpenChange={(o) => {
                    if (!o) setConfirmDeleteConversation(null);
                }}
                title={
                    confirmDeleteConversation !== null
                        ? t`Delete “${confirmDeleteConversation.title}”?`
                        : t`Delete?`
                }
                footer={
                    confirmDeleteConversation !== null ? (
                        <>
                            <Button
                                variant="ghost"
                                onClick={() =>
                                    setConfirmDeleteConversation(null)
                                }
                            >
                                {t`Cancel`}
                            </Button>
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setConfirmDeleteConversation(null);
                                    archiveConversationNow(
                                        confirmDeleteConversation.id,
                                    );
                                }}
                            >
                                {t`Archive instead`}
                            </Button>
                            <Button
                                variant="danger"
                                onClick={() =>
                                    deleteConversationNow(
                                        confirmDeleteConversation,
                                    )
                                }
                            >
                                {t`Delete`}
                            </Button>
                        </>
                    ) : null
                }
            >
                <Text as="p" tone="dim">
                    {t`The conversation leaves your rail. Nothing is lost — the whole thread is kept and you can bring it back.`}
                </Text>
            </Dialog>

            {/* The picker + drop overlay + drag-drop listeners (strangler-fig
                P1) — `openFilePicker` reaches the hidden `<input>` through this
                ref. */}
            <IngestHost
                ref={ingestHostRef}
                ports={ports}
                workspaceName={
                    library?.workspaceName ||
                    (displayName ? t`${displayName}'s workspace` : t`Workspace`)
                }
                onLibraryChanged={setLibrary}
                openRef={openRef}
                showNotice={showNotice}
                onIngestChanged={setIngest}
                onSourceAdded={(added, ingestId) => {
                    // A-07: ground it into the active conversation so the agent
                    // is aware of the new source. FIRST, so a send held on this
                    // upload (released by `source_added` below) flushes it.
                    groundAddedSource(added, ingestId);
                    sendChat({
                        type: "source_added",
                        source: { id: added.sourceId, title: added.title },
                        ingestId,
                    });
                    // DB4 transparent reuse: a byte-identical re-upload aliases
                    // silently to the existing Source (no new chip, no
                    // re-analysis) — surface a brief, non-blocking notice
                    // instead. The existing resource is already in the loaded
                    // library (it pre-dates this upload), so `refFor` resolves
                    // its REAL title — the wire's own `added.title` on this
                    // outcome mirrors the newly uploaded file's name, not the
                    // existing one's, so it is deliberately not used here.
                    // On the Studio canvas the same item is never there
                    // twice: its card is highlighted and brought into view.
                    // In the Studio, what was uploaded joins the project's
                    // canvas (beside what is there; a card already on it
                    // stays put).
                    if (onCanvas) studio.placeOnCanvas(added.sourceId);
                    if (added.type === "reused_existing") {
                        if (onCanvas) studio.revealOnCanvas(added.sourceId);
                        showNotice({
                            type: "source-deduped",
                            sourceId: added.sourceId,
                            existingTitle: refFor(added.sourceId)?.title,
                            onCanvas,
                        });
                    }
                }}
                onSourceFailed={(ingestId) =>
                    sendChat({ type: "source_upload_failed", ingestId })
                }
                applyIngest={applyIngest}
                activeConversationId={activeConversationId}
                onFilesQueued={recordIngestConversation}
            />

            <MessageLayer />
        </>
    );
};
